import { apiFetch } from './client';

export interface IvrTreeRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  name: string;
  root_node_id: string | null;
  active: boolean;
  /** Customer-scoped sequential number, displayed as `IVT-{N}` in the
   *  UI and used in URLs (`/ivr/trees/IVT-1`). Migration 0079. May be
   *  null on legacy rows that pre-date the migration (the backfill
   *  normally covers all of them — this is just defensive typing). */
  ref_number: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * Enriched row returned by the list endpoint — has the customer name and
 * counts so the list view can render status at a glance without a per-row
 * N+1 lookup.
 */
export interface IvrTreeSummaryRow extends IvrTreeRow {
  customer_name: string;
  did_count: number;
  node_count: number;
}

/**
 * Append `?customer_id=<id>` to a URL when an id is supplied. Used by every
 * single-tree endpoint (get / update / setRoot / applyTemplate / remove) so
 * a ref-form id like `IVT-7` resolves unambiguously across customers.
 *
 * Bugfix 2026-05-16: without the customer scope, `IVT-7` looked up the first
 * matching row across ALL visible customers (super_admin sees every
 * customer), so the wrong tree could load or — more commonly — RLS filtered
 * the picked row out and the request 404'd intermittently.
 */
function withCustomer(path: string, customerId?: string | null): string {
  if (!customerId) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}customer_id=${encodeURIComponent(customerId)}`;
}

export const ivrTrees = {
  list: (p: { customer_id?: string; search?: string; limit?: number; deleted?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (p.customer_id) q.set('customer_id', p.customer_id);
    if (p.search) q.set('search', p.search);
    if (p.limit) q.set('limit', String(p.limit));
    if (p.deleted) q.set('deleted', 'true');
    return apiFetch<{ rows: IvrTreeSummaryRow[] }>(`/api/ivr/trees?${q}`);
  },
  /** Un-delete a recycle-bin tree. Keyed by UUID (the recycle list returns UUIDs). */
  restore: (id: string, opts: { customerId?: string | null } = {}) =>
    apiFetch<IvrTreeRow>(withCustomer(`/api/ivr/trees/${id}/restore`, opts.customerId), {
      method: 'POST',
    }),
  get: (id: string, opts: { customerId?: string | null } = {}) =>
    apiFetch<IvrTreeRow>(withCustomer(`/api/ivr/trees/${id}`, opts.customerId)),
  create: (body: { name: string; customer_id?: string }) =>
    apiFetch<IvrTreeRow>('/api/ivr/trees', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: string, body: { name?: string; active?: boolean }, opts: { customerId?: string | null } = {}) =>
    apiFetch<IvrTreeRow>(withCustomer(`/api/ivr/trees/${id}`, opts.customerId), {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  setRoot: (id: string, node_id: string, opts: { customerId?: string | null } = {}) =>
    apiFetch<IvrTreeRow>(withCustomer(`/api/ivr/trees/${id}/root`, opts.customerId), {
      method: 'PUT',
      body: JSON.stringify({ node_id }),
    }),
  remove: (id: string, opts: { customerId?: string | null } = {}) =>
    apiFetch<void>(withCustomer(`/api/ivr/trees/${id}`, opts.customerId), { method: 'DELETE' }),
  listTemplates: () =>
    apiFetch<{ templates: Array<{ key: string; label: string; description: string }> }>(
      '/api/ivr/trees/templates',
    ),
  applyTemplate: (id: string, template_key: string, opts: { customerId?: string | null } = {}) =>
    apiFetch<{ nodes: unknown[]; edges: unknown[] }>(
      withCustomer(`/api/ivr/trees/${id}/apply-template`, opts.customerId),
      {
        method: 'POST',
        body: JSON.stringify({ template_key }),
      },
    ),
};
